import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../i18n/translations';
import { useLocation, useNavigate } from 'react-router-dom';

type Language = 'en' | 'fr';
type Translations = typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  changeLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Détermine la langue initiale en fonction de l'URL
  const getInitialLanguage = (): Language => {
    const path = location.pathname;
    if (path.startsWith('/fr')) return 'fr';
    if (path.startsWith('/en')) return 'en';
    
    // Si pas de préfixe de langue, utiliser la langue du navigateur ou par défaut
    const browserLang = navigator.language.split('-')[0];
    return (browserLang === 'fr') ? 'fr' : 'en';
  };
  
  const [language, setLanguage] = useState<Language>(getInitialLanguage());
  
  // Fonction pour changer de langue et mettre à jour l'URL
  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    
    // Met à jour l'URL avec le préfixe de langue
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(?:fr|en)/, '');
    const newPath = `/${lang}${pathWithoutLang || '/'}`;
    
    navigate(newPath);
  };
  
  // Effet pour synchroniser la langue avec l'URL
  useEffect(() => {
    const urlLang = getInitialLanguage();
    if (urlLang !== language) {
      setLanguage(urlLang);
    }
  }, [location.pathname]);

  const value = {
    language,
    setLanguage,
    changeLanguage,
    t: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}